import React, { Component } from 'react';
import classNames from 'classnames';

import { Divider } from '@eventbrite/eds-divider';

import Flag from './Flag';
import ImageContainer from './ImageContainer';
import PrimaryContent from './PrimaryContent';
import LeadingContent from './LeadingContent';
import SubContent from './SubContent';
import EventCardActions from './EventCardActions';
import { PromotedLabel } from '../PromotedLabel';

import { GRID_TYPE, EVENT_CARD_PROPTYPES, STANDARD_STYLE } from './constants';

import './gridEventCardContent.scss';

const DividerComponent = ({ shouldShowDivider, style }) => {
    let component = null;

    if (shouldShowDivider && style === STANDARD_STYLE) {
        component = (
            <div className="eds-event-card-content__divisor eds-l-pad-vert-4">
                <Divider color="ui-orange" />
            </div>
        );
    }

    return component;
};

const GridEventCardContentContainer = ({
    id,
    title,
    subtitle,
    description,
    onClick,
    linkUrl,
    linkAlt,
    style,
    shouldShowDivider,
    leadingContentOne,
    subContentOne,
    subContentTwo,
    subContentThree,
    subContentFour,
    subContentLast,
    primaryIconType,
    primaryIconStyle,
    primaryIconTitle,
    primaryIconOnClick,
    primaryIconDropdown,
    shouldShowIcons,
    isTargetBlank,
    isNoFollow,
    useSmallPadding,
    showLikesTooltip,
    saved,
    isPromoted,
    showRemoveTooltip,
    showMoreActions,
    showFewerEventsIcon,
    onEventCardMoreAction,
    bucketLabel,
    bucketKey,
    secondaryIconType,
    secondaryIconStyle,
    secondaryIconTitle,
    secondaryIconOnClick,
    isAuthenticated,
}) => (
    <div
        className={classNames(
            'eds-event-card-content__content-container eds-event-card-content__content-container--consumer',
            {
                'eds-event-card-content__content-container--small-padding':
                    useSmallPadding,
            },
        )}
    >
        <div className="eds-event-card-content--promoted-label">
            {isPromoted && <PromotedLabel />}
        </div>
        <div className="eds-event-card-content__content">
            <div
                className="eds-event-card-content__content__principal"
                data-event-bucket-label={bucketLabel}
                data-event-bucket-key={bucketKey}
            >
                <LeadingContent leadingContentOne={leadingContentOne} />

                <PrimaryContent
                    title={title}
                    subtitle={subtitle}
                    description={description}
                    onClick={onClick}
                    linkUrl={linkUrl}
                    linkAlt={linkAlt}
                    style={style}
                    isTargetBlank={isTargetBlank}
                    isNoFollow={isNoFollow}
                    type={GRID_TYPE}
                />

                <DividerComponent
                    shouldShowDivider={shouldShowDivider}
                    style={style}
                />

                <SubContent
                    subContentOne={subContentOne}
                    subContentTwo={subContentTwo}
                    subContentThree={subContentThree}
                    subContentFour={subContentFour}
                    subContentLast={subContentLast}
                ></SubContent>
            </div>
        </div>

        <EventCardActions
            id={id}
            primaryIconType={primaryIconType}
            primaryIconStyle={primaryIconStyle}
            primaryIconTitle={primaryIconTitle}
            primaryIconOnClick={primaryIconOnClick}
            primaryIconDropdown={primaryIconDropdown}
            shouldShowIcons={shouldShowIcons}
            showLikesTooltip={showLikesTooltip}
            saved={saved}
            showRemoveTooltip={showRemoveTooltip}
            showMoreActions={showMoreActions}
            showFewerEventsIcon={showFewerEventsIcon}
            onEventCardMoreAction={onEventCardMoreAction}
            secondaryIconType={secondaryIconType}
            secondaryIconStyle={secondaryIconStyle}
            secondaryIconTitle={secondaryIconTitle}
            secondaryIconOnClick={secondaryIconOnClick}
            isAuthenticated={isAuthenticated}
        />
    </div>
);

export default class GridEventCardContent extends Component {
    static propTypes = {
        ...EVENT_CARD_PROPTYPES,
    };

    state = {
        animatePrimaryButton: false,
    };

    _onPrimaryIconClick = () => {
        const { primaryIconOnClick } = this.props;

        this.setState(({ animatePrimaryButton }) => ({
            animatePrimaryButton: !animatePrimaryButton,
        }));

        primaryIconOnClick();
    };

    render() {
        const {
            id,
            style,
            imageStyle,
            imageUrl,
            isLazyImage,
            imageAlt,
            shouldHideImage,
            placeholderBgColor,
            onClick,
            onFocus,
            onBlur,
            linkUrl,
            title,
            subtitle,
            description,
            leadingContentOne,
            subContentOne,
            subContentTwo,
            subContentThree,
            subContentFour,
            subContentLast,
            flag,
            flagDark,
            shouldShowDivider,
            primaryIconType,
            primaryIconStyle,
            primaryIconTitle,
            secondaryIconType,
            secondaryIconStyle,
            secondaryIconTitle,
            secondaryIconOnClick,
            shouldShowIcons,
            containerSpacingClasses,
            isTargetBlank,
            isNoFollow,
            focused,
            useSmallPadding,
            showLikesTooltip,
            saved,
            isPromoted,
            showRemoveTooltip,
            showMoreActions,
            showFewerEventsIcon,
            onEventCardMoreAction,
            bucketLabel,
            bucketKey,
            isAuthenticated,
        } = this.props;

        const { animatePrimaryButton } = this.state;

        const classes = classNames(
            containerSpacingClasses,
            'eds-event-card-content',
            'eds-event-card-content--grid',
            `eds-event-card-content--${style}`,
            `eds-event-card-content--${imageStyle}`,
            {
                'eds-event-card-content--focused': focused,
                'eds-event-card-content--animate': animatePrimaryButton,
                'eds-event-card-content--right-crop': !!secondaryIconType,
            },
        );

        let imageContainer = null;

        if (!shouldHideImage) {
            imageContainer = (
                <ImageContainer
                    onClick={onClick}
                    linkUrl={linkUrl}
                    imageUrl={imageUrl}
                    isLazyImage={isLazyImage}
                    imageAlt={imageAlt}
                    flag={flag}
                    flagDark={flagDark}
                    imageStyle={imageStyle}
                    placeholderBgColor={placeholderBgColor}
                    isTargetBlank={isTargetBlank}
                    isNoFollow={isNoFollow}
                />
            );
        }

        return (
            <article
                className={classes}
                onFocus={onFocus}
                onBlur={onBlur}
                role="presentation"
            >
                {imageContainer}

                <GridEventCardContentContainer
                    id={id}
                    title={title}
                    subtitle={subtitle}
                    description={description}
                    onClick={onClick}
                    linkUrl={linkUrl}
                    linkAlt={imageAlt}
                    isTargetBlank={isTargetBlank}
                    isNoFollow={isNoFollow}
                    style={style}
                    shouldShowDivider={shouldShowDivider}
                    leadingContentOne={leadingContentOne}
                    subContentOne={subContentOne}
                    subContentTwo={subContentTwo}
                    subContentThree={subContentThree}
                    subContentFour={subContentFour}
                    subContentLast={subContentLast}
                    primaryIconType={primaryIconType}
                    primaryIconStyle={primaryIconStyle}
                    primaryIconTitle={primaryIconTitle}
                    primaryIconOnClick={this._onPrimaryIconClick}
                    shouldShowIcons={shouldShowIcons}
                    useSmallPadding={useSmallPadding}
                    showLikesTooltip={showLikesTooltip}
                    saved={saved}
                    isPromoted={isPromoted}
                    showRemoveTooltip={showRemoveTooltip}
                    showMoreActions={showMoreActions}
                    showFewerEventsIcon={showFewerEventsIcon}
                    onEventCardMoreAction={onEventCardMoreAction}
                    bucketLabel={bucketLabel}
                    bucketKey={bucketKey}
                    isAuthenticated={isAuthenticated}
                    secondaryIconType={
                        showFewerEventsIcon ? secondaryIconType : null
                    }
                    secondaryIconStyle={
                        showFewerEventsIcon ? secondaryIconStyle : null
                    }
                    secondaryIconTitle={
                        showFewerEventsIcon ? secondaryIconTitle : null
                    }
                    secondaryIconOnClick={
                        showFewerEventsIcon ? secondaryIconOnClick : null
                    }
                />

                <Flag flag={flag} flagDark={flagDark} />

                {!showFewerEventsIcon && (
                    <div className="eds-event-card-content__action--secondary">
                        <EventCardActions
                            id={id}
                            primaryIconType={secondaryIconType}
                            primaryIconStyle={secondaryIconStyle}
                            primaryIconTitle={secondaryIconTitle}
                            primaryIconOnClick={secondaryIconOnClick}
                            shouldShowIcons={shouldShowIcons}
                            showLikesTooltip={showLikesTooltip}
                            saved={saved}
                        />
                    </div>
                )}
            </article>
        );
    }
}
